import { REGEX_ESCAPE_SPECIAL_CHARS } from './constant'
import { getLocaleCodeFromUrl } from './localeUtils'
import {
  IS_BROWSER,
  getLocalStorage,
  setLocalStorage,
  deleteFromLocalStorage,
} from 'config/appConfig'

// Replacement pattern for regex escaping - keeps $& reference for matched text
const REGEX_REPLACEMENT = String.raw`\$&`

/**
 * Promo Stacking Service
 * Shared logic for handling promo code stacking across components
 * Used by: MyaccountWallet, PromoWalletCheckout
 */

export class PromoStackingService {
  constructor({ disablePromoStacking, promoCodeRegexToAllowStacking }) {
    this.disablePromoStacking = disablePromoStacking
    this.stackingPattern = promoCodeRegexToAllowStacking
  }

  /**
   * Check if a promo code is allowed to stack based on regex pattern
   * @param {string} promoCode - The promo code to check
   * @returns {boolean} - True if promo can stack with others
   */
  isPromoAllowedToStack(promoCode) {
    const normalizedPromoCode = promoCode?.trim?.() || ''
    const stackingPattern = this.stackingPattern

    if (!normalizedPromoCode) return false
    if (typeof stackingPattern !== 'string') return false
    const normalizedPattern = stackingPattern.trim()
    if (!normalizedPattern) return false

    try {
      const startsWithRegex = normalizedPattern.startsWith('^')
        ? normalizedPattern
        : `^${normalizedPattern.replace(REGEX_ESCAPE_SPECIAL_CHARS, REGEX_REPLACEMENT)}`
      return new RegExp(startsWithRegex).test(normalizedPromoCode)
    } catch (error) {
      console.error('Invalid promo stacking pattern:', error)
      return false
    }
  }

  /**
   * Determine if a promo should replace an existing one
   * @param {string} newPromoCode - The new promo code being applied
   * @param {Array} appliedPromoCodeList - List of currently applied promos
   * @returns {Object} - { shouldReplace, promoToReplace, canApplyDirectly, alreadyApplied }
   */
  determineReplacementStrategy(newPromoCode, appliedPromoCodeList = []) {
    // Check if promo is already applied
    const alreadyApplied = appliedPromoCodeList.some(
      promo => promo?.code === newPromoCode
    )

    if (alreadyApplied) {
      return {
        shouldReplace: false,
        promoToReplace: null,
        canApplyDirectly: false,
        alreadyApplied: true,
      }
    }

    // If stacking is enabled or no promos exist, can apply directly
    if (!this.disablePromoStacking || appliedPromoCodeList.length === 0) {
      return {
        shouldReplace: false,
        promoToReplace: null,
        canApplyDirectly: true,
        alreadyApplied: false,
      }
    }

    // Stacking is disabled and promos exist - determine replacement
    const isIncomingStacking = this.isPromoAllowedToStack(newPromoCode)
    const existingStackingPromo = appliedPromoCodeList.find(promo =>
      this.isPromoAllowedToStack(promo?.code)
    )
    const existingNonStackingPromo = appliedPromoCodeList.find(
      promo => promo?.code && !this.isPromoAllowedToStack(promo?.code)
    )

    // Can apply if no conflict (stacking + non-stacking can coexist)
    const canApplyDirectly =
      (isIncomingStacking && !existingStackingPromo) ||
      (!isIncomingStacking && !existingNonStackingPromo)

    if (canApplyDirectly) {
      return {
        shouldReplace: false,
        promoToReplace: null,
        canApplyDirectly: true,
        alreadyApplied: false,
      }
    }

    // Determine which promo to replace
    const promoToReplace = isIncomingStacking
      ? existingStackingPromo?.code
      : existingNonStackingPromo?.code

    return {
      shouldReplace: !!promoToReplace,
      promoToReplace,
      canApplyDirectly: false,
      alreadyApplied: false,
    }
  }
}

const extractCountryCodeFromLocale = locale => {
  if (typeof locale !== 'string') return ''
  const localeParts = locale.split('_')
  if (localeParts.length < 2) return ''
  return localeParts[1] || ''
}

const resolveCountryCodeSafely = () => {
  try {
    return extractCountryCodeFromLocale(getLocaleCodeFromUrl())
  } catch (error) {
    console.error('Error while generating Promo code key:', error)
    return ''
  }
}

/**
 * Generate a scoped storage key for applied promo codes based on current country
 * Used when promo stacking is disabled to maintain promo state per market/country
 * @param {boolean} disablePromoStacking - Whether promo stacking is disabled
 * @returns {string|null} - Scoped key like 'appliedPromoCode_US' or null if stacking enabled
 */
export const getScopedAppliedPromoKey = disablePromoStacking => {
  if (!disablePromoStacking) return null
  const countryCode = resolveCountryCodeSafely()
  if (!countryCode) return null
  return `appliedPromoCode_${countryCode}`
}

const isMigrationEnabled = newKey => {
  return !!newKey && IS_BROWSER
}

const getLegacyPromoStorageKey = () => {
  return `appliedPromoCode_${encodeURIComponent(window.location.pathname)}`
}

const getLegacyPromoStorageData = oldKey => {
  return {
    oldValue: getLocalStorage(oldKey),
    oldPrev: getLocalStorage(`${oldKey}_previous`),
    oldCurrent: getLocalStorage(`${oldKey}_current`),
  }
}

const hasValidLegacyPromoValue = oldValue => {
  return typeof oldValue === 'string' && !!oldValue
}

const setMigratedPromoStorageValues = (
  newKey,
  oldValue,
  oldPrev,
  oldCurrent
) => {
  setLocalStorage(newKey, oldValue)
  if (oldPrev) setLocalStorage(`${newKey}_previous`, oldPrev)
  if (oldCurrent) setLocalStorage(`${newKey}_current`, oldCurrent)
}

const clearLegacyPromoStorageValues = oldKey => {
  deleteFromLocalStorage(oldKey)
  deleteFromLocalStorage(`${oldKey}_previous`)
  deleteFromLocalStorage(`${oldKey}_current`)
}

/**
 * Migrate promo storage entries from the old path-encoded key format to the
 * new country-code-based key format introduced in COMX-3101.
 *
 * Old format: appliedPromoCode_%2Fcart  (encodeURIComponent of pathname)
 * New format: appliedPromoCode_US       (country code extracted from locale)
 *
 * Migration is best-effort and runs only once: if the new key already has a
 * value the migration is skipped. Old keys are deleted after migration.
 *
 * @param {string|null} newKey - The new country-code-based storage key.
 */
export const migrateOldPromoStorageKey = newKey => {
  if (!isMigrationEnabled(newKey)) return
  try {
    if (getLocalStorage(newKey)) return
    const oldKey = getLegacyPromoStorageKey()
    const { oldValue, oldPrev, oldCurrent } = getLegacyPromoStorageData(oldKey)
    if (!hasValidLegacyPromoValue(oldValue)) return
    setMigratedPromoStorageValues(newKey, oldValue, oldPrev, oldCurrent)
    clearLegacyPromoStorageValues(oldKey)
  } catch (error) {
    console.error('Error while migrating promo storage key:', error)
  }
}
